// src/components/layout/Dashboard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  PlusCircle, 
  FileText, 
  Users,
  User,
  ChevronDown,
  LogOut, 
  BarChart3, 
  Settings,
  BookOpen,
  Calendar,
  TrendingUp,
  ArrowLeft,
  GraduationCap,
  Calculator,
  Target,
  Brain,
  Award,
  ChevronRight,
  Home,
  Filter,
  Grid3X3,
  UserPlus,
  UserCheck,
  School,
  Clock,
  CheckCircle2,
  AlertCircle,
  Activity,
  Sparkles,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  BookMarked,
  ClipboardCheck,
  PieChart,
  TrendingDown,
  RefreshCw,
  X,
  ExternalLink,
  Play,
  HelpCircle,
  Layers
} from 'lucide-react';
import AssessmentTaskForm from '../forms/TaskForm';
import TaskSubmissionForm from '../forms/SubmissionForm';
import LearnerMarksForm from '../forms/TaskMarksForm';
import PerformanceAnalysis from '../charts/PerformanceAnalysis';
import GradePerformanceAnalysis from '../charts/GradePerformanceAnalysis';
import TasksList from './TasksList';
import SubmissionsList from './SubmissionsList';
import SubmissionDetail from './SubmissionDetail';
import TaskDetail from './TaskDetail';
import TaskMarksView from './TaskMarksView';
import ReportsGenerator from '../reports/ReportsGenerator';
import AttendanceManager from '../attendance/AttendanceManager';
import AttendanceForm from '../forms/AttendanceForm';
import AttendanceList from '../attendance/AttendanceList';
import AttendanceDetail from '../attendance/AttendanceDetail';
import AnalysisTabWrapper from '../charts/AnalysisWrapper';
import SessionForm from '../forms/SessionForm';
import SessionsList from '../sessions/SessionsList';
import SessionDetail from '../sessions/SessionDetail';
import { SessionService } from '../../services/sessionService';
import { AttendanceService } from '../../services/attendanceService';
import { AssessmentService } from '../../services/taskService';
import { LearnerService } from '../../services/learnerService';
import { ReportsService } from '../../services/reportsService';
import { useAuth } from '../../contexts/AuthContext';
import { useContextualData } from '../../hooks/useContextualData';
import logo from '../../assets/images/logo.png';

// ============================================================================
// DASHBOARD INSIGHTS COMPONENT
// ============================================================================
const DashboardInsights = ({ 
  stats, 
  recentActivity, 
  onNavigate, 
  onQuickAction,
  showGuide,
  setShowGuide 
}) => {
  const [animatedStats, setAnimatedStats] = useState({});

  // Animate numbers on mount
  useEffect(() => {
    const animateValue = (key, end, duration = 1000) => {
      let start = 0;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setAnimatedStats(prev => ({ ...prev, [key]: end }));
          clearInterval(timer);
        } else {
          setAnimatedStats(prev => ({ ...prev, [key]: Math.floor(start) }));
        }
      }, 16);
    };

    Object.entries(stats).forEach(([key, value]) => {
      if (typeof value === 'number') {
        animateValue(key, value);
      }
    });
  }, [stats]);

  const insightCards = [
    {
      id: 'tasks',
      title: 'Assessment Tasks',
      value: animatedStats.totalTasks ?? 0,
      subtitle: `${stats.activeTasks || 0} active`,
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-600',
      trend: stats.tasksTrend,
      action: () => onNavigate('tasks')
    },
    {
      id: 'submissions',
      title: 'Submissions',
      value: animatedStats.totalSubmissions ?? 0,
      subtitle: `${stats.pendingSubmissions || 0} pending review`,
      icon: ClipboardCheck,
      color: 'from-emerald-500 to-emerald-600',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      trend: stats.submissionsTrend,
      action: () => onNavigate('submissions')
    },
    {
      id: 'learners',
      title: 'Learners',
      value: animatedStats.totalLearners ?? 0,
      subtitle: `${stats.activeGrades || 0} grades`,
      icon: GraduationCap,
      color: 'from-violet-500 to-violet-600',
      bgLight: 'bg-violet-50',
      textColor: 'text-violet-600',
      trend: stats.learnersTrend,
      action: () => onNavigate('learners')
    },
    {
      id: 'sessions',
      title: 'Sessions',
      value: animatedStats.totalSessions ?? 0,
      subtitle: `${stats.upcomingSessions || 0} upcoming`,
      icon: Calendar,
      color: 'from-amber-500 to-amber-600',
      bgLight: 'bg-amber-50',
      textColor: 'text-amber-600',
      trend: stats.sessionsTrend,
      action: () => onNavigate('sessions')
    },
    {
      id: 'attendance',
      title: 'Attendance Rate',
      value: `${animatedStats.attendanceRate ?? 0}%`,
      subtitle: `${stats.totalAttendanceRecords || 0} records`,
      icon: UserCheck,
      color: 'from-cyan-500 to-cyan-600',
      bgLight: 'bg-cyan-50',
      textColor: 'text-cyan-600',
      trend: stats.attendanceTrend,
      action: () => onNavigate('attendance')
    },
    {
      id: 'reports',
      title: 'AI Reports',
      value: animatedStats.totalReports ?? 0,
      subtitle: 'Generated insights',
      icon: Brain,
      color: 'from-pink-500 to-pink-600',
      bgLight: 'bg-pink-50',
      textColor: 'text-pink-600',
      trend: stats.reportsTrend,
      action: () => onNavigate('reports')
    }
  ];

  const quickActions = [
    { id: 'new-task', label: 'Create Task', icon: PlusCircle, color: 'bg-blue-600 hover:bg-blue-700', action: () => onQuickAction('new-task') },
    { id: 'new-submission', label: 'Add Submission', icon: ClipboardCheck, color: 'bg-emerald-600 hover:bg-emerald-700', action: () => onQuickAction('new-submission') },
    { id: 'new-session', label: 'Plan Session', icon: Calendar, color: 'bg-amber-600 hover:bg-amber-700', action: () => onQuickAction('new-session') },
    { id: 'new-learner', label: 'Add Learner', icon: UserPlus, color: 'bg-violet-600 hover:bg-violet-700', action: () => onQuickAction('new-learner') },
    { id: 'generate-report', label: 'AI Report', icon: Brain, color: 'bg-pink-600 hover:bg-pink-700', action: () => onQuickAction('generate-report') },
  ];

  const TrendIndicator = ({ trend }) => {
    if (!trend || trend === 0) return null;
    const isPositive = trend > 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
        {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {Math.abs(trend)}%
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-violet-900 rounded-2xl p-6 sm:p-8">
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,transparent,black)]" />
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl" />
        
        <div className="relative">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">Welcome back</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Dashboard Overview
              </h1>
              <p className="text-blue-100/80 text-sm sm:text-base max-w-xl">
                Track your educational data, monitor learner progress, and generate AI-powered insights all in one place.
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowGuide(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl text-white text-sm font-medium transition-all"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Platform Guide</span>
              </button>
            </div>
          </div>
          
          {/* Quick Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            {[
              { label: 'Avg Performance', value: `${stats.avgPerformance || 0}%`, icon: TrendingUp },
              { label: 'This Week', value: stats.thisWeekSubmissions || 0, icon: Activity },
              { label: 'Pending Marks', value: stats.pendingMarks || 0, icon: Clock },
              { label: 'Active Subjects', value: stats.activeSubjects || 0, icon: BookOpen }
            ].map((stat, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <stat.icon className="w-4 h-4 text-blue-200" />
                  <span className="text-blue-100/70 text-xs">{stat.label}</span>
                </div>
                <p className="text-white font-bold text-lg">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Quick Actions
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={action.action}
              className={`${action.color} text-white rounded-xl p-3 sm:p-4 flex flex-col items-center gap-2 transition-all hover:scale-105 hover:shadow-lg`}
            >
              <action.icon className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-xs sm:text-sm font-medium text-center">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Insight Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {insightCards.map((card) => (
          <button
            key={card.id}
            onClick={card.action}
            className="group bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-gray-200 transition-all text-left"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`${card.bgLight} p-3 rounded-xl`}>
                <card.icon className={`w-6 h-6 ${card.textColor}`} />
              </div>
              <div className="flex items-center gap-2">
                <TrendIndicator trend={card.trend} />
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-all" />
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">{card.title}</p>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-gray-400 text-xs mt-1">{card.subtitle}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Two Column Layout: Activity & Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Recent Activity
            </h3>
            <button className="text-blue-600 text-sm font-medium hover:text-blue-700">
              View all
            </button>
          </div>
          
          {recentActivity.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No recent activity</p>
              <p className="text-gray-400 text-xs mt-1">Start by creating a task or adding submissions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentActivity.slice(0, 5).map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className={`p-2 rounded-lg ${activity.bgColor || 'bg-blue-50'}`}>
                    {activity.icon && <activity.icon className={`w-4 h-4 ${activity.iconColor || 'text-blue-500'}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                    <p className="text-xs text-gray-500">{activity.description}</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{activity.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Performance Overview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-violet-500" />
              Performance Distribution
            </h3>
          </div>
          
          {stats.performanceDistribution ? (
            <div className="space-y-4">
              {stats.performanceDistribution.map((level, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">{level.label}</span>
                    <span className="text-sm font-medium text-gray-900">{level.count} ({level.percentage}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${level.color}`}
                      style={{ width: `${level.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <PieChart className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No performance data yet</p>
              <p className="text-gray-400 text-xs mt-1">Add marks to see performance distribution</p>
            </div>
          )}
        </div>
      </div>

      {/* Subject-Grade Cards */}
      {stats.subjectGradeStats && stats.subjectGradeStats.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Layers className="w-5 h-5 text-emerald-500" />
              Subject & Grade Overview
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.subjectGradeStats.map((combo, idx) => (
              <button
                key={idx}
                onClick={() => onNavigate('tasks', combo)}
                className="group p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all text-left"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    <span className="font-medium text-gray-900">{combo.subject}</span>
                  </div>
                  <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                    Grade {combo.grade}
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-gray-900">{combo.taskCount || 0}</p>
                    <p className="text-xs text-gray-500">Tasks</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-gray-900">{combo.learnerCount || 0}</p>
                    <p className="text-xs text-gray-500">Learners</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-emerald-600">{combo.avgPerformance || 0}%</p>
                    <p className="text-xs text-gray-500">Avg</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PLATFORM GUIDE MODAL
// ============================================================================
const PlatformGuideModal = ({ isOpen, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);

  const guideSteps = [
    {
      title: 'Welcome to SmartRegister',
      description: 'Your comprehensive platform for managing educational assessments, tracking learner progress, and generating AI-powered insights.',
      icon: Sparkles,
      color: 'from-blue-500 to-violet-500',
      tips: [
        'Navigate using the tabs at the top',
        'Use quick actions for common tasks',
        'Click on any insight card to dive deeper'
      ]
    },
    {
      title: 'Assessment Tasks',
      description: 'Create and manage assessment tasks with detailed marking criteria, file attachments, and due dates.',
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      tips: [
        'Click "Create Task" to add a new assessment',
        'Set itemized marks for detailed grading',
        'Attach resources and memos for learners'
      ]
    },
    {
      title: 'Submissions & Marking',
      description: 'Track learner submissions and record marks with detailed per-item scoring.',
      icon: ClipboardCheck,
      color: 'from-emerald-500 to-emerald-600',
      tips: [
        'Add submissions after learners complete tasks',
        'Use itemized marking for detailed feedback',
        'View submission history per learner'
      ]
    },
    {
      title: 'Learner Management',
      description: 'Manage your learner database with profiles, enrolled subjects, and performance tracking.',
      icon: GraduationCap,
      color: 'from-violet-500 to-violet-600',
      tips: [
        'Add learners individually or in bulk',
        'Assign subjects and grades to each learner',
        'Track individual progress over time'
      ]
    },
    {
      title: 'Sessions & Attendance',
      description: 'Plan teaching sessions, track attendance, and monitor learner engagement.',
      icon: Calendar,
      color: 'from-amber-500 to-amber-600',
      tips: [
        'Create sessions with lesson plans',
        'Record attendance for each session',
        'View attendance analytics and trends'
      ]
    },
    {
      title: 'AI-Powered Reports',
      description: 'Generate intelligent performance reports with actionable insights and recommendations.',
      icon: Brain,
      color: 'from-pink-500 to-pink-600',
      tips: [
        'Choose from grade, subject, or learner reports',
        'Get AI-generated insights and recommendations',
        'Export reports for parents and stakeholders'
      ]
    }
  ];

  const currentStep = guideSteps[activeStep];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${currentStep.color} p-6 text-white`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <currentStep.icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-white/80 text-sm">Step {activeStep + 1} of {guideSteps.length}</p>
              <h2 className="text-xl font-bold">{currentStep.title}</h2>
            </div>
          </div>
          <p className="text-white/90">{currentStep.description}</p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            Quick Tips
          </h4>
          <ul className="space-y-2 mb-6">
            {currentStep.tips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
          
          {/* Step Indicators */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {guideSteps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === activeStep ? 'bg-blue-500 w-6' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
              disabled={activeStep === 0}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {activeStep === guideSteps.length - 1 ? (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-violet-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
              >
                Get Started
              </button>
            ) : (
              <button
                onClick={() => setActiveStep(prev => Math.min(guideSteps.length - 1, prev + 1))}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
const Dashboard = () => {
  const { userProfile, activeContext, signOut, userContexts } = useAuth();
  const { tasks, submissions, marks, loading, activeContext: dataContext } = useContextualData();
  
  // Navigation state
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  
  // Subject-Grade filtering
  const [selectedSubjectGrade, setSelectedSubjectGrade] = useState(null);
  const [subjectGradeStats, setSubjectGradeStats] = useState([]);
  
  // Data state
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedAttendanceSession, setSelectedAttendanceSession] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewMode, setViewMode] = useState('list'); 
  const [formMode, setFormMode] = useState(null); 
  const [selectedSubmissionForMarks, setSelectedSubmissionForMarks] = useState(null);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalTasks: 0,
    activeTasks: 0,
    totalSubmissions: 0,
    pendingSubmissions: 0,
    totalLearners: 0,
    activeGrades: 0,
    totalSessions: 0,
    upcomingSessions: 0,
    attendanceRate: 0,
    totalAttendanceRecords: 0,
    totalReports: 0,
    avgPerformance: 0,
    thisWeekSubmissions: 0,
    pendingMarks: 0,
    activeSubjects: 0,
    subjectGradeStats: [],
    performanceDistribution: null
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  // All data for filtering
  const [allTasks, setAllTasks] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [allMarks, setAllMarks] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [allLearners, setAllLearners] = useState([]);

  // Tabs configuration
  const tabs = selectedSubjectGrade ? [
    { id: 'tasks', label: 'Tasks', icon: FileText, description: `${selectedSubjectGrade.subject} Grade ${selectedSubjectGrade.grade} Tasks` },
    { id: 'submissions', label: 'Submissions', icon: BookOpen, description: `${selectedSubjectGrade.subject} Grade ${selectedSubjectGrade.grade} Submissions` },
    { id: 'analysis', label: 'Analysis', icon: BarChart3, description: `${selectedSubjectGrade.subject} Grade ${selectedSubjectGrade.grade} Analysis` },
    { id: 'attendance', label: 'Attendance', icon: Users, description: `${selectedSubjectGrade.subject} Grade ${selectedSubjectGrade.grade} Attendance` },
    { id: 'reports', label: 'Reports', icon: TrendingUp, description: `${selectedSubjectGrade.subject} Grade ${selectedSubjectGrade.grade} Reports` }
  ] : [
    { id: 'overview', label: 'Overview', icon: Home, description: 'Dashboard overview and quick access' },
    { id: 'tasks', label: 'Tasks', icon: FileText, description: 'Assessment tasks and assignments' },
    { id: 'submissions', label: 'Submissions', icon: BookOpen, description: 'Student submissions and responses' },
    { id: 'analysis', label: 'Analysis', icon: BarChart3, description: 'Performance analytics and insights' },
    { id: 'sessions', label: 'Sessions', icon: Calendar, description: 'Teaching sessions planning' },
    { id: 'attendance', label: 'Attendance', icon: Users, description: 'Class attendance tracking' },
    { id: 'learners', label: 'Learners', icon: GraduationCap, description: 'Learner management and profiles' },
    { id: 'reports', label: 'Reports', icon: TrendingUp, description: 'Performance reports and insights' }
  ];

  // Load dashboard statistics
  const loadDashboardStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      // Load all data in parallel
      const [tasksData, sessionsData] = await Promise.all([
        AssessmentService.getAssessmentTasks().catch(() => []),
        SessionService.getUserSessions().catch(() => [])
      ]);

      setAllTasks(tasksData);
      setAllSessions(sessionsData);

      // Load submissions for all tasks
      let allSubmissionsData = [];
      let allMarksData = [];
      const subjectGradeMap = new Map();

      for (const task of tasksData) {
        try {
          const taskSubmissions = await AssessmentService.getTaskSubmissions(task.id);
          const taskMarks = await AssessmentService.getLearnerMarks(task.id);
          allSubmissionsData = [...allSubmissionsData, ...taskSubmissions];
          allMarksData = [...allMarksData, ...taskMarks];

          // Build subject-grade stats
          const key = `${task.subject}-${task.grade}`;
          if (!subjectGradeMap.has(key)) {
            subjectGradeMap.set(key, {
              subject: task.subject,
              grade: task.grade,
              taskCount: 0,
              submissionCount: 0,
              learnerSet: new Set(),
              totalMarks: 0,
              marksCount: 0
            });
          }
          const stats = subjectGradeMap.get(key);
          stats.taskCount++;
          stats.submissionCount += taskSubmissions.length;
          taskMarks.forEach(mark => {
            stats.learnerSet.add(`${mark.learnerSurname}-${mark.learnerName}`);
            if (mark.totalMarks && mark.marksObtained !== undefined) {
              stats.totalMarks += (mark.marksObtained / mark.totalMarks) * 100;
              stats.marksCount++;
            }
          });
        } catch (error) {
          console.warn(`Error loading data for task ${task.id}:`, error);
        }
      }

      setAllSubmissions(allSubmissionsData);
      setAllMarks(allMarksData);

      // Calculate subject-grade stats
      const subjectGradeStatsArray = Array.from(subjectGradeMap.values()).map(stat => ({
        ...stat,
        learnerCount: stat.learnerSet.size,
        avgPerformance: stat.marksCount > 0 ? Math.round(stat.totalMarks / stat.marksCount) : 0
      }));

      // Calculate performance distribution
      let performanceDistribution = null;
      if (allMarksData.length > 0) {
        const levels = [
          { label: 'Outstanding (80-100%)', min: 80, max: 100, color: 'bg-emerald-500', count: 0 },
          { label: 'Excellent (70-79%)', min: 70, max: 79, color: 'bg-blue-500', count: 0 },
          { label: 'Good (60-69%)', min: 60, max: 69, color: 'bg-cyan-500', count: 0 },
          { label: 'Adequate (50-59%)', min: 50, max: 59, color: 'bg-amber-500', count: 0 },
          { label: 'Partial (40-49%)', min: 40, max: 49, color: 'bg-orange-500', count: 0 },
          { label: 'Not Achieved (0-39%)', min: 0, max: 39, color: 'bg-red-500', count: 0 }
        ];

        allMarksData.forEach(mark => {
          if (mark.totalMarks && mark.marksObtained !== undefined) {
            const percentage = (mark.marksObtained / mark.totalMarks) * 100;
            const level = levels.find(l => percentage >= l.min && percentage <= l.max);
            if (level) level.count++;
          }
        });

        const totalMarks = allMarksData.length;
        performanceDistribution = levels.map(level => ({
          ...level,
          percentage: totalMarks > 0 ? Math.round((level.count / totalMarks) * 100) : 0
        }));
      }

      // Calculate overall stats
      const uniqueSubjects = new Set(tasksData.map(t => t.subject));
      const uniqueGrades = new Set(tasksData.map(t => t.grade));
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const thisWeekSubmissions = allSubmissionsData.filter(s => {
        const date = s.date?.toDate ? s.date.toDate() : new Date(s.date);
        return date >= oneWeekAgo;
      }).length;

      const upcomingSessions = sessionsData.filter(s => {
        const date = s.sessionDate?.toDate ? s.sessionDate.toDate() : new Date(s.sessionDate);
        return date >= now;
      }).length;

      // Calculate average performance
      let avgPerformance = 0;
      if (allMarksData.length > 0) {
        const totalPercentage = allMarksData.reduce((sum, mark) => {
          if (mark.totalMarks && mark.marksObtained !== undefined) {
            return sum + (mark.marksObtained / mark.totalMarks) * 100;
          }
          return sum;
        }, 0);
        avgPerformance = Math.round(totalPercentage / allMarksData.length);
      }

      // Get unique learners count
      const uniqueLearners = new Set();
      allMarksData.forEach(mark => {
        uniqueLearners.add(`${mark.learnerSurname}-${mark.learnerName}`);
      });

      // Load reports count
      let reportsCount = 0;
      try {
        const reports = await ReportsService.getSavedReports();
        reportsCount = reports.length;
      } catch (error) {
        console.warn('Error loading reports:', error);
      }

      setDashboardStats({
        totalTasks: tasksData.length,
        activeTasks: tasksData.filter(t => !t.archived).length,
        totalSubmissions: allSubmissionsData.length,
        pendingSubmissions: allSubmissionsData.filter(s => !s.marked).length,
        totalLearners: uniqueLearners.size,
        activeGrades: uniqueGrades.size,
        totalSessions: sessionsData.length,
        upcomingSessions,
        attendanceRate: 0, // Would need attendance data
        totalAttendanceRecords: 0,
        totalReports: reportsCount,
        avgPerformance,
        thisWeekSubmissions,
        pendingMarks: allSubmissionsData.filter(s => !s.marked).length,
        activeSubjects: uniqueSubjects.size,
        subjectGradeStats: subjectGradeStatsArray,
        performanceDistribution
      });

      // Build recent activity
      const activities = [];
      
      // Add recent tasks
      tasksData.slice(0, 3).forEach(task => {
        activities.push({
          title: task.taskName || task.name,
          description: `${task.subject} - Grade ${task.grade}`,
          icon: FileText,
          bgColor: 'bg-blue-50',
          iconColor: 'text-blue-500',
          time: formatRelativeTime(task.createdAt),
          timestamp: task.createdAt
        });
      });

      // Add recent submissions
      allSubmissionsData.slice(0, 3).forEach(sub => {
        activities.push({
          title: `Submission recorded`,
          description: `${sub.learnerSurname || 'Learner'} - ${sub.taskName || 'Task'}`,
          icon: ClipboardCheck,
          bgColor: 'bg-emerald-50',
          iconColor: 'text-emerald-500',
          time: formatRelativeTime(sub.date || sub.createdAt),
          timestamp: sub.date || sub.createdAt
        });
      });

      // Add recent sessions
      sessionsData.slice(0, 2).forEach(session => {
        activities.push({
          title: session.topic || 'Session',
          description: `${session.subject} - Grade ${session.grade}`,
          icon: Calendar,
          bgColor: 'bg-amber-50',
          iconColor: 'text-amber-500',
          time: formatRelativeTime(session.createdAt),
          timestamp: session.createdAt
        });
      });

      // Sort by timestamp and take top 5
      activities.sort((a, b) => {
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return dateB - dateA;
      });

      setRecentActivity(activities.slice(0, 5));

    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return '';
    const d = date?.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  // Load stats on mount
  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  // Check for first-time user
  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('SmartRegister-guide-seen');
    if (!hasSeenGuide && dashboardStats.totalTasks === 0) {
      setShowGuide(true);
      localStorage.setItem('SmartRegister-guide-seen', 'true');
    }
  }, [dashboardStats.totalTasks]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle navigation from insights
  const handleInsightsNavigate = (tab, subjectGrade = null) => {
    if (tab === 'learners') {
      navigate('/learners');
    } else if (subjectGrade) {
      setSelectedSubjectGrade(subjectGrade);
      setActiveTab(tab);
    } else {
      setActiveTab(tab);
    }
  };

  // Handle quick actions
  const handleQuickAction = (action) => {
    switch (action) {
      case 'new-task':
        setFormMode('new-task');
        setActiveTab('tasks');
        break;
      case 'new-submission':
        setFormMode('new-submission');
        setActiveTab('submissions');
        break;
      case 'new-session':
        setFormMode('new-session');
        setActiveTab('sessions');
        break;
      case 'new-learner':
        navigate('/learners/new');
        break;
      case 'generate-report':
        setActiveTab('reports');
        break;
      default:
        break;
    }
  };

  // Handle back from subject-grade view
  const handleBackToOverview = () => {
    setSelectedSubjectGrade(null);
    setActiveTab('overview');
    setSelectedTask(null);
    setSelectedSubmission(null);
    setFormMode(null);
    setViewMode('list');
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  // Filter data based on selected subject-grade
  const getFilteredTasks = () => {
    if (!selectedSubjectGrade) return allTasks;
    return allTasks.filter(task => 
      task.subject === selectedSubjectGrade.subject && 
      task.grade === selectedSubjectGrade.grade
    );
  };

  const getFilteredSubmissions = () => {
    if (!selectedSubjectGrade) return allSubmissions;
    const filteredTaskIds = getFilteredTasks().map(t => t.id);
    return allSubmissions.filter(sub => filteredTaskIds.includes(sub.taskId));
  };

  // Render tab content
  const renderTabContent = () => {
    // Show loading
    if (statsLoading && activeTab === 'overview') {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
            <p className="text-gray-500">Loading dashboard data...</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <DashboardInsights
            stats={dashboardStats}
            recentActivity={recentActivity}
            onNavigate={handleInsightsNavigate}
            onQuickAction={handleQuickAction}
            showGuide={showGuide}
            setShowGuide={setShowGuide}
          />
        );

      case 'tasks':
        if (formMode === 'new-task') {
          return (
            <AssessmentTaskForm
              onSave={() => {
                setFormMode(null);
                loadDashboardStats();
              }}
              onCancel={() => setFormMode(null)}
            />
          );
        }
        if (selectedTask && viewMode === 'detail') {
          return (
            <TaskDetail
              task={selectedTask}
              onBack={() => {
                setSelectedTask(null);
                setViewMode('list');
              }}
              onEdit={() => setViewMode('edit')}
            />
          );
        }
        return (
          <TasksList
            tasks={getFilteredTasks()}
            onSelectTask={(task) => {
              setSelectedTask(task);
              setViewMode('detail');
            }}
            onCreateTask={() => setFormMode('new-task')}
          />
        );

      case 'submissions':
        if (formMode === 'new-submission') {
          return (
            <TaskSubmissionForm
              tasks={getFilteredTasks()}
              onSave={() => {
                setFormMode(null);
                loadDashboardStats();
              }}
              onCancel={() => setFormMode(null)}
            />
          );
        }
        if (selectedSubmission && viewMode === 'detail') {
          return (
            <SubmissionDetail
              submission={selectedSubmission}
              onBack={() => {
                setSelectedSubmission(null);
                setViewMode('list');
              }}
              onAddMarks={() => {
                setSelectedSubmissionForMarks(selectedSubmission);
                setViewMode('marks');
              }}
            />
          );
        }
        if (viewMode === 'marks' && selectedSubmissionForMarks) {
          return (
            <LearnerMarksForm
              submission={selectedSubmissionForMarks}
              onSave={() => {
                setSelectedSubmissionForMarks(null);
                setViewMode('list');
                loadDashboardStats();
              }}
              onCancel={() => {
                setSelectedSubmissionForMarks(null);
                setViewMode('detail');
              }}
            />
          );
        }
        return (
          <SubmissionsList
            submissions={getFilteredSubmissions()}
            onSelectSubmission={(submission) => {
              setSelectedSubmission(submission);
              setViewMode('detail');
            }}
            onCreateSubmission={() => setFormMode('new-submission')}
          />
        );

      case 'analysis':
        return selectedSubjectGrade ? (
          <GradePerformanceAnalysis
            grade={selectedSubjectGrade.grade}
            subject={selectedSubjectGrade.subject}
            tasks={getFilteredTasks()}
            marks={allMarks.filter(m => {
              const task = allTasks.find(t => t.id === m.taskId);
              return task?.subject === selectedSubjectGrade.subject && 
                     task?.grade === selectedSubjectGrade.grade;
            })}
          />
        ) : (
          <AnalysisTabWrapper
            tasks={allTasks}
            marks={allMarks}
          />
        );

      case 'sessions':
        if (formMode === 'new-session') {
          return (
            <SessionForm
              onSave={() => {
                setFormMode(null);
                loadDashboardStats();
              }}
              onCancel={() => setFormMode(null)}
            />
          );
        }
        if (selectedSession && viewMode === 'detail') {
          return (
            <SessionDetail
              session={selectedSession}
              onBack={() => {
                setSelectedSession(null);
                setViewMode('list');
              }}
            />
          );
        }
        return (
          <SessionsList
            sessions={allSessions}
            onSelectSession={(session) => {
              setSelectedSession(session);
              setViewMode('detail');
            }}
            onCreateSession={() => setFormMode('new-session')}
          />
        );

      case 'attendance':
        if (selectedAttendanceSession) {
          return (
            <AttendanceDetail
              session={selectedAttendanceSession}
              onBack={() => setSelectedAttendanceSession(null)}
            />
          );
        }
        return (
          <AttendanceManager
            onSelectSession={(session) => setSelectedAttendanceSession(session)}
          />
        );

      case 'learners':
        return (
          <div className="text-center py-12">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Learner Management</h3>
            <p className="text-gray-500 mb-4">Manage your learners, profiles, and enrollments</p>
            <button
              onClick={() => navigate('/learners')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              Go to Learners
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        );

      case 'reports':
        return <ReportsGenerator />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Back Button */}
            <div className="flex items-center gap-3">
              {selectedSubjectGrade && (
                <button
                  onClick={handleBackToOverview}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
              )}
              <Link to="/" className="flex items-center gap-2">
                <img src={logo} alt="SmartRegister" className="h-12 w-auto" />
                
              </Link>
              {selectedSubjectGrade && (
                <div className="hidden sm:flex items-center gap-2 ml-4 px-3 py-1 bg-blue-50 rounded-lg">
                  <BookOpen className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    {selectedSubjectGrade.subject} - Grade {selectedSubjectGrade.grade}
                  </span>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-500 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="hidden sm:inline text-sm font-medium text-gray-700">
                  {userProfile?.displayName || 'User'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSelectedTask(null);
                    setSelectedSubmission(null);
                    setFormMode(null);
                    setViewMode('list');
                  }}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderTabContent()}
        </div>
      </main>

      {/* Platform Guide Modal */}
      <PlatformGuideModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
      />
    </div>
  );
};

export default Dashboard;